/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import Range from 'rc-slider'
import 'rc-slider/assets/index.css'
import React, { useCallback, useState } from 'react'
import { useDebounceCallback } from '../../../hooks/useDebounceCallback'
import { LoadingSpinner } from '../shared/LoadingSpinner'
import style from './FilterRange.module.sass'

export const FilterRange = React.memo(function FilterRange(props: {
	name: string
	constraints: null | { min?: null | number; max?: null | number }
	value: null | { min?: null | number; max?: null | number }
	fallback?: null | { min?: null | number; max?: null | number }
	onChange: (update: { min?: null | number; max?: null | number }) => void
	onChangeDebounced: (update: { min?: null | number; max?: null | number }) => void
	dirty?: { min?: boolean; max?: boolean }
	unit?: 'CZK' | '%' | string
	coefficient?: number
	formatter?: string
	step?: number
	decimalPlaces?: number
}) {
	const {
		name,
		constraints,
		value,
		fallback,
		onChange,
		onChangeDebounced,
		dirty,
		unit,
		coefficient,
		decimalPlaces,
		formatter,
		step,
	} = props

	const maxMinLimit = value?.min ?? constraints?.min ?? 0
	const minMaxLimit = value?.max ?? constraints?.max ?? Number.POSITIVE_INFINITY

	const values = (['min', 'max'] as const).map((key) => {
		const visibleValue = value?.[key] ?? fallback?.[key] ?? constraints?.[key] ?? 0
		return visibleValue
	})

	const formatUnit = useCallback(() => {
		if (unit) {
			if (unit === '-') {
				return <span className={style.missingUnit} />
			}
			return unit
		}
		switch (formatter) {
			case 'percent':
			case 'score':
				return '%'
			case 'price':
			case 'priceDER':
				return 'Kč'
			case 'weight':
				return 'kg'
		}
	}, [formatter, unit])

	const visibleValues = {
		min: value?.min ?? fallback?.min ?? constraints?.min ?? 0,
		max: value?.max ?? fallback?.max ?? constraints?.max ?? 0,
	}

	const [numberItem, setNumberItem] = useState<'min' | 'max' | null>(null)

	const debounceChangeHandler = useDebounceCallback(onChangeDebounced, 300)

	return (
		<>
			<Range
				className={style.root}
				allowCross={false}
				range
				ariaLabelForHandle={name}
				step={step}
				onChange={(state) => {
					const newValues = state as number[]
					const minLimitedValue = Math.min(minMaxLimit, newValues[0])
					const maxLimitedValue = Math.max(maxMinLimit, newValues[1])
					onChange({ min: minLimitedValue, max: maxLimitedValue })
					debounceChangeHandler?.([{ min: minLimitedValue, max: maxLimitedValue }])
				}}
				value={values}
				defaultValue={[maxMinLimit, minMaxLimit]} // initial handle positions
				min={constraints?.min ?? 0}
				max={constraints?.max ?? 100}
			/>
			<div className={style.valuesWrapper}>
				{(['min', 'max'] as const).map((key, index) => {
					const visibleValue = value?.[key] ?? fallback?.[key] ?? constraints?.[key] ?? 0

					const inputValue = Number(visibleValue * (coefficient ?? 1))
						.toFixed(decimalPlaces ?? 2)
						.replace('.00', '')

					return (
						<span key={key} className={style.label}>
							{index === 1 && (
								<>
									<LoadingSpinner active={dirty?.[key]} />
								</>
							)}
							<input
								type={numberItem === key ? 'number' : 'text'}
								onFocus={() => setNumberItem(key)}
								onBlur={() => setNumberItem(null)}
								className={style.input}
								min={Number(constraints?.min)}
								max={Number(constraints?.max)}
								onChange={(e) => {
									const update = {
										min: visibleValues.min,
										max: visibleValues.max,
									}
									update[key] = Number(e.target.value) / (coefficient ?? 1) / 1

									onChange(update)
									debounceChangeHandler?.([update])
								}}
								value={numberItem === key ? inputValue : Intl.NumberFormat('cs-CZ').format(Number(inputValue))}
							/>

							<span className={style.unit}>{formatUnit()}</span>

							{index === 0 && (
								<>
									<LoadingSpinner active={dirty?.[key]} />{' '}
								</>
							)}
						</span>
					)
				})}
			</div>
		</>
	)
})
